import type * as React from 'react'
import { useCallback, useState } from 'react'

import { getPublishUrl } from '@buffer-mono/app-shell/src/common/utils/urls'
import { Service } from '@buffer-mono/app-shell/src/exports/Orchestrator/channelConnections/types'
import { Button, CoachMark, SimpleSpinner } from '@buffer-mono/popcorn'

import type { CreateCallback } from '~publish/legacy/composer/interfaces/types'

import AppActionCreators from '../../action-creators/AppActionCreators'
import AppStore from '../../stores/AppStore'

interface ThreadsLocationRefreshCoachmarkProps {
  open: boolean
  onDismiss: () => void
  channelIds: string[]
  saveAsDraft: CreateCallback
  children: React.ReactNode
}

export const ThreadsLocationRefreshCoachmark: React.FC<
  ThreadsLocationRefreshCoachmarkProps
> = ({ open, onDismiss, channelIds, saveAsDraft, children }) => {
  const [isSavingAsDraft, setIsSavingAsDraft] = useState(false)

  const handleRefreshChannel = useCallback((): void => {
    const orgData = AppStore.getOrganizationsData()
    const channelId = channelIds[0] // Use the first selected Threads channel

    if (!channelId) {
      return
    }

    const isNewPostMode = AppStore.getIsNewPostMode()

    // Try to use appshell actions if available
    if (window.appshell?.actions && window.appshell.actions.connectChannel) {
      // Check if we can save the draft first
      const { isSavingPossible, isSavingDraftPossible } = AppStore.getAppState()

      if (!isSavingPossible && !isSavingDraftPossible) {
        // If we can't save (e.g., empty post), redirect back to the queue page
        const destinationUrl = getPublishUrl(`channels/${channelId}`)

        setIsSavingAsDraft(true)
        window.appshell.actions.connectChannel({
          selectedService: Service.threads,
          isRefreshingConnection: true,
          skipCelebrationModal: true,
          cta: 'publish-composer-threadsLocation-refresh-1',
          selectedRefreshChannelId: channelId,
          role: orgData.selected.isAdmin ? 'admin' : 'member',
          destinationUrl,
        })
        return
      }

      // If we can save, save first then connect
      setIsSavingAsDraft(true)

      const savePromise = isNewPostMode
        ? saveAsDraft()
        : AppActionCreators.saveDrafts('SAVE', {
            shouldSkipEmptyTextAlert: true,
            hasCampaignsToTagsFeature: true,
          })

      savePromise
        .then(({ successfulResponses }) => {
          const updateId = successfulResponses[0]?.updates?.find(
            (update) => update.profile_id === channelId,
          )?.id

          const destinationUrl = getPublishUrl(
            `channels/${channelId}${
              updateId ? `/posts/${updateId}/edit` : '?tab=drafts'
            }`,
          )

          return window.appshell?.actions?.connectChannel({
            selectedService: Service.threads,
            isRefreshingConnection: true,
            skipCelebrationModal: true,
            cta: 'publish-composer-threadsLocation-refresh-1',
            selectedRefreshChannelId: channelId,
            role: orgData.selected.isAdmin ? 'admin' : 'member',
            destinationUrl,
          })
        })
        .catch(() => {
          setIsSavingAsDraft(false)
        })
    } else {
      // When appshell is not available, save and redirect to settings
      setIsSavingAsDraft(true)

      const savePromise = isNewPostMode
        ? saveAsDraft()
        : AppActionCreators.saveDrafts('SAVE', {
            shouldSkipEmptyTextAlert: true,
            hasCampaignsToTagsFeature: true,
          })

      savePromise
        // eslint-disable-next-line promise/always-return
        .then((): void => {
          // Redirect to channels settings page where user can refresh manually
          window.location.href = getPublishUrl('settings/channels')
        })
        .catch(() => {
          setIsSavingAsDraft(false)
        })
    }
  }, [channelIds, saveAsDraft])

  return (
    <CoachMark open={open} onDismiss={onDismiss}>
      <CoachMark.Overlay style={{ display: 'inline' }}>
        {children}
      </CoachMark.Overlay>

      <CoachMark.Content>
        <CoachMark.Title>{`Let's setup Threads locations!`}</CoachMark.Title>
        <CoachMark.Description>
          To get everything ready for adding locations, a quick channel refresh
          is needed.
        </CoachMark.Description>
        <CoachMark.Footer>
          <CoachMark.Dismiss>
            <Button variant="tertiary">Not Now</Button>
          </CoachMark.Dismiss>
          <Button
            variant="primary"
            disabled={isSavingAsDraft}
            onClick={handleRefreshChannel}
          >
            {isSavingAsDraft ? (
              <>
                <SimpleSpinner /> Saving as Draft
              </>
            ) : (
              'Refresh Channel'
            )}
          </Button>
        </CoachMark.Footer>
      </CoachMark.Content>
    </CoachMark>
  )
}
